import { useCallback, useReducer, useRef } from "react";
import { Drafts } from "../editorContent";

export enum DraftHistoryAction {
  Load = "load",
  Change = "change",
  Undo = "undo",
  Redo = "redo",
}

const COALESCE_MS = 500;

interface HistoryState {
  present: Drafts;
  past: Drafts[];
  future: Drafts[];
}

type Action =
  | { type: DraftHistoryAction.Load; drafts: Drafts }
  | { type: DraftHistoryAction.Change; drafts: Drafts; coalesce: boolean }
  | { type: DraftHistoryAction.Undo }
  | { type: DraftHistoryAction.Redo };

const reducer = (state: HistoryState, action: Action): HistoryState => {
  switch (action.type) {
    case DraftHistoryAction.Load:
      return { present: action.drafts, past: [], future: [] };
    case DraftHistoryAction.Change:
      return {
        present: action.drafts,
        past: action.coalesce ? state.past : [...state.past, state.present],
        future: [],
      };
    case DraftHistoryAction.Undo:
      if (!state.past.length) return state;
      return {
        present: state.past[state.past.length - 1],
        past: state.past.slice(0, -1),
        future: [state.present, ...state.future],
      };
    case DraftHistoryAction.Redo:
      if (!state.future.length) return state;
      return {
        present: state.future[0],
        past: [...state.past, state.present],
        future: state.future.slice(1),
      };
    default:
      return state;
  }
};

export interface DraftHistory {
  drafts: Drafts;
  canUndo: boolean;
  canRedo: boolean;
  load: (drafts: Drafts) => void;
  change: (drafts: Drafts) => void;
  undo: () => void;
  redo: () => void;
}

/**
 * Undo/redo history for the per-locale editor drafts. Rapid successive changes
 * (typing) within COALESCE_MS collapse into a single history step.
 */
export function useDraftHistory(): DraftHistory {
  const [state, dispatch] = useReducer(reducer, {
    present: {},
    past: [],
    future: [],
  });
  const lastChangeAt = useRef(0);

  const load = useCallback(
    (drafts: Drafts) => dispatch({ type: DraftHistoryAction.Load, drafts }),
    []
  );

  const change = useCallback((drafts: Drafts) => {
    const now = Date.now();
    const coalesce = now - lastChangeAt.current < COALESCE_MS;
    lastChangeAt.current = now;
    dispatch({ type: DraftHistoryAction.Change, drafts, coalesce });
  }, []);

  const undo = useCallback(
    () => dispatch({ type: DraftHistoryAction.Undo }),
    []
  );
  const redo = useCallback(
    () => dispatch({ type: DraftHistoryAction.Redo }),
    []
  );

  return {
    drafts: state.present,
    canUndo: state.past.length > 0,
    canRedo: state.future.length > 0,
    load,
    change,
    undo,
    redo,
  };
}
